import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchQuizzes,
  postUserAnswer,
  resetUserAnswersState,
  setSelectedQuiz,
} from "../store/reducers/quizzesSlice";
import {
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import FormPageHeader from "../admin/quizSystem/components/FormPageHeader";
import {
  DEFAULT_COUNTRY_LANGUAGE_CODE,
  findDefaultCountryLanguageOption,
} from "../utils/defaultCountryLanguage";

const getQuestionText = (question) =>
  question?.question || question?.question_en || question?.question_dk || "";

const getOptionText = (option) =>
  option?.text || option?.text_en || option?.text_dk || "";

const findQuizByRoute = (quizzes, routeId, category) => {
  const isSameRoute = (quiz) =>
    `${quiz?.routeId || quiz?.quizKey || quiz?.id}` === `${routeId}`;
  const isSameCategory = (quiz) => !category || quiz?.category === category;

  const matches = quizzes.filter((quiz) => isSameRoute(quiz));
  const exactMatch = matches.find((quiz) => isSameCategory(quiz));

  if (exactMatch) return exactMatch;

  return (
    matches.find((quiz) => quiz.category === "organization") ||
    matches[0] ||
    null
  );
};

const mergeCountryLanguageOptions = (...optionLists) => {
  const optionMap = new Map();

  optionLists.flat().forEach((option) => {
    if (!option?.combined_code && option?.id == null) return;

    const key =
      option?.id != null
        ? `id:${option.id}`
        : `code:${option.combined_code}`;

    optionMap.set(key, option);
  });

  return Array.from(optionMap.values());
};

/* ─── Component ─────────────────────────────────────────────────────────────── */
const QuizDetail = () => {
  const { id, category } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const { quizzes, selectedQuiz, userAnswers } = useSelector(
    (state) => state.quizzes,
  );

  const [answers, setAnswers] = useState({});
  const [quizData, setQuizData] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [countryLanguageId, setCountryLanguageId] = useState("");
  const [persistedDefaultCountryLanguage, setPersistedDefaultCountryLanguage] =
    useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const pendingNavigation = useRef(null);
  const unblockRef = useRef(null);
  const answersRef = useRef(answers);
  const currentIndexRef = useRef(currentIndex);
  const defaultLanguageAppliedRef = useRef(false);
  const organizationId = localStorage.getItem("organisation_id");
  const selectedCategory = quizData?.category || selectedQuiz?.category || category;
  const categoryLanguages =
    quizzes.countryLanguagesByCategory?.[selectedCategory] || [];
  const countryLanguageOptions = useMemo(
    () =>
      mergeCountryLanguageOptions(
        categoryLanguages,
        quizzes.countryLanguages || [],
        persistedDefaultCountryLanguage ? [persistedDefaultCountryLanguage] : [],
      ),
    [categoryLanguages, persistedDefaultCountryLanguage, quizzes.countryLanguages],
  );
  const defaultCountryLanguageOption = useMemo(
    () => findDefaultCountryLanguageOption(countryLanguageOptions),
    [countryLanguageOptions],
  );

  useEffect(() => {
    const defaultOption = findDefaultCountryLanguageOption(
      mergeCountryLanguageOptions(categoryLanguages, quizzes.countryLanguages || []),
    );

    if (defaultOption?.id) {
      setPersistedDefaultCountryLanguage(defaultOption);
    }
  }, [categoryLanguages, quizzes.countryLanguages]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  /* ── data loading ── */
  useEffect(() => {
    if (quizzes.data.length > 0) {
      const found = findQuizByRoute(quizzes.data, id, category);
      if (found) {
        setQuizData(found);
        if (selectedQuiz !== found) {
          dispatch(setSelectedQuiz(found));
        }
      }
    } else if (
      findQuizByRoute(
        [selectedQuiz].filter(Boolean),
        id,
        category || "organization",
      )
    ) {
      setQuizData(selectedQuiz);
    } else {
      dispatch(fetchQuizzes({ orgId: organizationId, countryLanguageId }));
    }
  }, [
    id,
    category,
    selectedQuiz,
    quizzes.data,
    dispatch,
    organizationId,
    countryLanguageId,
  ]);

  useEffect(() => {
    if (
      countryLanguageId &&
      countryLanguageOptions.length &&
      !countryLanguageOptions.some(
        (item) => `${item.id}` === `${countryLanguageId}`,
      )
    ) {
      setCountryLanguageId("");
      defaultLanguageAppliedRef.current = false;
    }
  }, [
    countryLanguageId,
    countryLanguageOptions,
  ]);

  useEffect(() => {
    if (
      defaultLanguageAppliedRef.current ||
      !defaultCountryLanguageOption?.id ||
      `${countryLanguageId}` === `${defaultCountryLanguageOption.id}`
    ) {
      return;
    }

    defaultLanguageAppliedRef.current = true;
    setCountryLanguageId(`${defaultCountryLanguageOption.id}`);
    setAnswers({});
    setCurrentIndex(0);
    dispatch(
      fetchQuizzes({
        orgId: organizationId,
        countryLanguageId: defaultCountryLanguageOption.id,
      }),
    );
  }, [
    countryLanguageId,
    defaultCountryLanguageOption?.id,
    dispatch,
    organizationId,
  ]);

  /* ── navigation block ── */
  useEffect(() => {
    if (!quizData) return;

    unblockRef.current = history.block((location) => {
      const hasProgress =
        Object.keys(answersRef.current).length > 0 ||
        currentIndexRef.current > 0;

      if (hasProgress) {
        setOpenDialog(true);
        pendingNavigation.current = location;
        return false;
      }
      return true;
    });

    return () => {
      if (unblockRef.current) unblockRef.current();
    };
  }, [quizData]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (
        quizData &&
        Object.keys(answers).length > 0 &&
        Object.keys(answers).length !== quizData.questions.length
      ) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [answers, quizData]);

  if (quizzes.loading || !quizData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <CircularProgress sx={{ color: "#E05A4E" }} />
      </div>
    );
  }

  const questions = quizData.questions || [];
  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const progressPercent = totalQuestions
    ? Math.round((answeredCount / totalQuestions) * 100)
    : 0;

  /* ── answer handling ── */
  const handleChange = (qId, optionId, isMulti) => {
    setAnswers((prev) => {
      if (!isMulti) return { ...prev, [qId]: [optionId] };
      const prevOptions = prev[qId] || [];
      if (prevOptions.includes(optionId)) {
        return { ...prev, [qId]: prevOptions.filter((o) => o !== optionId) };
      }
      return { ...prev, [qId]: [...prevOptions, optionId] };
    });
  };

  /* ── navigation ── */
  const goTo = (idx) => {
    if (idx >= 0 && idx < totalQuestions) setCurrentIndex(idx);
  };

  const handleBackClick = () => {
    const hasProgress = answeredCount > 0 || currentIndex > 0;
    if (hasProgress) {
      setOpenDialog(true);
    } else {
      history.push("/quizzes");
    }
  };

  const handleConfirmLeave = () => {
    setOpenDialog(false);
    if (unblockRef.current) unblockRef.current();
    history.push(pendingNavigation.current || "/quizzes");
  };

  const handleCancelLeave = () => {
    setOpenDialog(false);
    pendingNavigation.current = null;
  };

  /* ── submit ── */
  const handleSubmit = async () => {
    if (answeredCount !== totalQuestions) {
      toast.error("Please answer all questions");
      return;
    }
    dispatch(resetUserAnswersState());
    const formattedAnswers = Object.keys(answers).map((qId) => ({
      question_id: Number(qId),
      ids: answers[qId],
    }));
    let payload = {
      organization_id: +organizationId,
      answers: formattedAnswers,
    };
    if (quizData.video_id) payload.video_id = quizData.video_id;
    else if (quizData.card_id) payload.card_id = quizData.card_id;
    else if (quizData.quiz_id) payload.quiz_id = quizData.quiz_id;

    const response = await dispatch(postUserAnswer(payload));
    if (postUserAnswer.rejected.match(response)) {
      toast.error(
        response.payload?.error ||
          response.payload?.message ||
          "Quiz submission failed",
      );
      return;
    }
    const message = response.payload?.message || "Quiz submitted successfully";
    message.toLowerCase().includes("fail")
      ? toast.error(message)
      : toast.success(message);
    if (unblockRef.current) unblockRef.current();
    history.push("/quizzes");
  };

  const isSelected = (qId, optId) => answers[qId]?.includes(optId) || false;
  const handleCountryLanguageChange = async (event) => {
    const selectedValue = event.target.value;

    setCountryLanguageId(selectedValue);
    setAnswers({});
    setCurrentIndex(0);

    const response = await dispatch(
      fetchQuizzes({
        orgId: organizationId,
        countryLanguageId: selectedValue,
      }),
    );

    if (fetchQuizzes.rejected.match(response)) {
      toast.error(
        response.payload?.error ||
          response.payload?.message ||
          "Unable to load quiz for selected country/language",
      );
    }
  };

  /* ─── render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen py-3 px-2 sm:p-6 font-sans box-border">
      <FormPageHeader title={quizData.displayName} onBack={handleBackClick}>
        {countryLanguageOptions.length ? (
          <select
            value={countryLanguageId}
            onChange={handleCountryLanguageChange}
            aria-label="Country language"
            className="min-h-[38px] min-w-[160px] rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#E05A4E]"
          >
            {!countryLanguageId && (
              <option value="">
                {DEFAULT_COUNTRY_LANGUAGE_CODE}
              </option>
            )}
            {countryLanguageOptions.map((item) => (
              <option key={`${item.id}-${item.combined_code}`} value={item.id}>
                {item.combined_code}
              </option>
            ))}
          </select>
        ) : null}
      </FormPageHeader>

      {/* ── Two-column layout ── */}
      <div className="flex gap-3 items-start flex-wrap">
        {/* ── Question Card ── */}
        <div className="flex-1 min-w-[300px] bg-white border-2 border-[#E05A4E] rounded-2xl p-6 sm:p-7 box-border">
          <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200">
            Question ID: {currentQuestion?.id}
          </span>

          <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200">
            Version: {currentQuestion?.version}
          </span>
        </div>

          {/* Question text */}
          <div className="text-lg sm:text-xl font-bold text-gray-900 mb-5 leading-snug">
            Q{currentIndex + 1}: {getQuestionText(currentQuestion)}
          </div>

          {/* Options */}
          <div className="flex flex-col gap-2.5">
            {currentQuestion.correct_answers === 1
              ? currentQuestion.options?.map((opt) => {
                  const selected = isSelected(currentQuestion.id, opt.id);
                  return (
                    <div
                      key={opt.id}
                      onClick={() =>
                        handleChange(currentQuestion.id, opt.id, false)
                      }
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border cursor-pointer select-none transition-all duration-150
                        ${
                          selected
                            ? "border-[#E05A4E] bg-[#FEF0EF]"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                    >
                      {/* Radio circle */}
                      <div
                        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                          selected
                            ? "border-[#E05A4E] bg-[#E05A4E]"
                            : "border-white bg-black"
                        }`}
                      >
                        {selected ? (
                          <div className="h-2 w-2 rounded-full bg-white" />
                        ) : (
                          <div className="h-3 w-3 rounded-full bg-white" />
                        )}
                      </div>
                      <span
                        className={`text-sm sm:text-base transition-colors
                          ${
                            selected
                              ? "font-semibold text-[#E05A4E]"
                              : "text-gray-900"
                          }`}
                      >
                        {getOptionText(opt)}
                      </span>
                    </div>
                  );
                })
              : currentQuestion.options?.map((opt) => {
                  const selected = isSelected(currentQuestion.id, opt.id);
                  return (
                    <div
                      key={opt.id}
                      onClick={() =>
                        handleChange(currentQuestion.id, opt.id, true)
                      }
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border cursor-pointer select-none transition-all duration-150
                        ${
                          selected
                            ? "border-[#E05A4E] bg-[#FEF0EF]"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                    >
                      {/* Checkbox */}
                      <div
                        className={`flex h-[18px] w-[18px] items-center justify-center rounded-[4px] border-2 ${
                          selected
                            ? "border-[#E05A4E] bg-[#E05A4E]"
                            : "border !border-black bg-[#f8dfdf]"
                        }`}
                      >
                        {selected && (
                          <span className="text-[11px] font-bold leading-none text-white">
                            ✓
                          </span>
                        )}
                      </div>
                      <span
                        className={`text-sm sm:text-base transition-colors
                          ${
                            selected
                              ? "font-semibold text-[#E05A4E]"
                              : "text-gray-900"
                          }`}
                      >
                        {getOptionText(opt)}
                      </span>
                    </div>
                  );
                })}
          </div>

          {/* ── Nav row ── */}
          <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
            <button
              onClick={handleSubmit}
              disabled={userAnswers.loading || answeredCount !== totalQuestions}
              className={`px-6 py-2.5 bg-[#E05A4E] text-white rounded-lg text-sm sm:text-base font-semibold border-none transition-opacity
                ${
                  userAnswers.loading || answeredCount !== totalQuestions
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:opacity-90 cursor-pointer"
                }`}
            >
              {userAnswers.loading ? "Submitting…" : "Submit Quiz"}
            </button>

            <div className="flex gap-2">
              <button
                onClick={() => goTo(currentIndex - 1)}
                disabled={currentIndex === 0}
                aria-label="Previous question"
                className={`w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg bg-white text-gray-600 cursor-pointer transition-colors
                  ${
                    currentIndex === 0
                      ? "opacity-35 cursor-not-allowed"
                      : "hover:bg-gray-50"
                  }`}
              >
                <FaChevronLeft size={13} />
              </button>
              <button
                onClick={() => goTo(currentIndex + 1)}
                disabled={currentIndex === totalQuestions - 1}
                aria-label="Next question"
                className={`w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg bg-white text-gray-600 cursor-pointer transition-colors
                  ${
                    currentIndex === totalQuestions - 1
                      ? "opacity-35 cursor-not-allowed"
                      : "hover:bg-gray-50"
                  }`}
              >
                <FaChevronRight size={13} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Progress Panel ── */}
        <div className="w-full sm:w-[260px] flex-shrink-0 bg-[#F6605F] rounded-2xl px-3 py-4 text-white box-border">
          {/* Progress bar */}
          <div className="h-1.5 rounded-full bg-white/30 mb-4 overflow-hidden">
            <div
              className="h-full rounded-full bg-white transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Stats */}
          <div className="flex justify-between text-sm text-white font-medium mb-1.5">
            <span className="text-white">Questions Answered</span>
            <strong>
              {answeredCount} / {totalQuestions}
            </strong>
          </div>
          <div className="flex justify-between text-sm font-medium text-white">
            <span className="text-white">Current Score</span>
            <strong>{progressPercent}%</strong>
          </div>

          {/* Question dots */}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {questions.map((q, idx) => {
              const answered = !!answers[q.id];
              const isCurrent = idx === currentIndex;
              return (
                <div
                  key={idx}
                  onClick={() => goTo(idx)}
                  title={`Question ${idx + 1}`}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white cursor-pointer transition-all border-2
                    ${
                      isCurrent
                        ? "bg-white !text-[#E05A4E] border-white"
                        : answered
                        ? "bg-white/55 border-transparent"
                        : "bg-white/25 border-transparent hover:bg-white/35"
                    }`}
                >
                  {idx + 1}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Leave Dialog ── */}
      <Dialog
        open={openDialog}
        onClose={handleCancelLeave}
        PaperProps={{ className: "rounded-2xl" }}
      >
        <DialogTitle className="font-bold text-gray-900">
          Leave Quiz?
        </DialogTitle>
        <DialogContent>
          <Typography className="text-gray-600">
            Are you sure you want to leave the quiz? Your progress will be lost.
          </Typography>
        </DialogContent>
        <DialogActions className="px-4 pb-4 gap-2">
          <Button
            onClick={handleConfirmLeave}
            sx={{
              color: "#1976d2",
              textTransform: "none",
              background: "none",
              border: "none",
              boxShadow: "none",
              "&:hover": { background: "none" },
            }}
          >
            Yes
          </Button>
          <Button
            onClick={handleCancelLeave}
            sx={{
              color: "#E05A4E",
              textTransform: "none",
              background: "none",
              border: "none",
              boxShadow: "none",
              "&:hover": { background: "none" },
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default QuizDetail;
